import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  AUTH_CHANGED_EVENT,
  getSavedJobs,
  isAuthenticated,
  unsaveJob
} from '../utils/userData';
import './SavedJobs.css';

const SavedJobs = () => {
  const navigate = useNavigate();
  const [savedJobs, setSavedJobs] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated());

  useEffect(() => {
    const syncData = () => {
      const userLoggedIn = isAuthenticated();
      setIsLoggedIn(userLoggedIn);
      setSavedJobs(userLoggedIn ? getSavedJobs() : []);
    };

    syncData();
    window.addEventListener(AUTH_CHANGED_EVENT, syncData);

    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, syncData);
    };
  }, []);

  const formatSalary = (min, max) => {
    if (!min || !max) return 'Competitive package';
    return `INR ${(min / 100000).toFixed(0)}L - INR ${(max / 100000).toFixed(0)}L`;
  };

  const formatSavedDate = (savedAt) => {
    if (!savedAt) return 'Recently';

    try {
      return new Date(savedAt).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'Recently';
    }
  };

  const getCompanyName = (job) => {
    return job.company?.display_name ||
      job.company?.displayName ||
      job.company?.name ||
      (typeof job.company === 'string' ? job.company : 'Company');
  };

  const getLocation = (job) => {
    return job.location?.display_name ||
      job.location?.displayName ||
      job.location?.name ||
      (typeof job.location === 'string' ? job.location : 'Location');
  };

  const handleViewDetails = (job) => {
    navigate(`/jobs/${job.id}`, { state: { job } });
  };

  const handleRemove = (jobId) => {
    const removed = unsaveJob(jobId);
    if (!removed) {
      return;
    }

    setSavedJobs((prev) => prev.filter((job) => String(job.id) !== String(jobId)));
    toast.info('Removed from saved jobs.');
  };

  return (
    <div className="saved-jobs-page">
      <Navbar />

      <main className="saved-jobs-container">
        <header className="saved-jobs-header">
          <h1>Saved Jobs</h1>
          <p>Keep your shortlisted roles in one place and revisit them anytime.</p>
        </header>

        {!isLoggedIn ? (
          <section className="saved-jobs-locked">
            <h2>Sign in to access your saved jobs</h2>
            <p>Your shortlist appears only after login or signup.</p>
            <div className="locked-actions">
              <button className="btn-login" onClick={() => navigate('/job-login')}>
                Login
              </button>
              <button className="btn-signup" onClick={() => navigate('/job-signup')}>
                Create Account
              </button>
            </div>
          </section>
        ) : savedJobs.length === 0 ? (
          <section className="saved-jobs-empty">
            <h2>No saved jobs yet</h2>
            <p>Tap the heart icon on any role to save it here.</p>
            <button className="btn-explore" onClick={() => navigate('/jobs')}>
              Explore Opportunities
            </button>
          </section>
        ) : (
          <section className="saved-jobs-grid">
            {savedJobs.map((job) => (
              <article className="saved-job-card" key={job.id}>
                <div className="saved-job-top">
                  <h3>{job.title || 'Untitled Role'}</h3>
                  <span>Saved on {formatSavedDate(job.savedAt)}</span>
                </div>

                <p className="saved-job-company">{getCompanyName(job)}</p>
                <p className="saved-job-location">{getLocation(job)}</p>
                <p className="saved-job-salary">{formatSalary(job.salary_min, job.salary_max)}</p>

                {job.description && (
                  <p className="saved-job-description">
                    {job.description.substring(0, 150)}...
                  </p>
                )}

                <div className="saved-job-actions">
                  <button onClick={() => handleViewDetails(job)} className="btn-details">
                    View Details
                  </button>
                  <button onClick={() => handleRemove(job.id)} className="btn-remove">
                    Remove
                  </button>
                </div>
              </article>
            ))}
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default SavedJobs;
